import React, { useState, useEffect } from 'react';
import { Users, UserPlus, Trash2, Edit2, Shield, ShieldOff, AlertTriangle, CheckCircle2, X, Save, Eye, EyeOff, Mail, Send, Download, Upload, Gift, XCircle, Calculator as CalcFeatureIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { authHelper } from '../utils/authHelper';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminPanel() {
  const { t, language } = useLanguage();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/users`, { 
        headers: authHelper.getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      } else {
        setError(t.admin.errorLoading);
      }
    } catch (err) {
      setError(t.admin.connectionError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    try {
      const res = await fetch(`${API_URL}/api/admin/users`, {
        method: 'POST',
        headers: authHelper.getAuthHeaders(),
        body: JSON.stringify({ username, email, password, isAdmin })
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(`${t.admin.user} ${username} ${t.admin.createdSuccess}. ${t.admin.code}: ${data.recoveryCode}`);
        setShowAddModal(false);
        resetForm();
        fetchUsers();
      } else {
        setError(data.detail || data.error || t.admin.errorCreating);
      }
    } catch (err) {
      setError(t.admin.connectionError);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    setError('');
    setSuccess('');
    
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${editingUser.id}`, {
        method: 'PUT',
        headers: authHelper.getAuthHeaders(),
        body: JSON.stringify({ username, email, password: password || undefined, isAdmin })
      });
      if (res.ok) {
        setSuccess(`${t.admin.user} ${username} ${t.admin.updatedSuccess}`);
        setEditingUser(null);
        setShowAddModal(false);
        resetForm();
        fetchUsers();
      } else {
        const data = await res.json();
        setError(data.detail || data.error || t.admin.errorUpdating);
      }
    } catch (err) {
      setError(t.admin.connectionError);
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm(t.admin.deleteConfirm)) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${id}`, { 
        method: 'DELETE', 
        headers: authHelper.getAuthHeaders()
      });
      if (res.ok) {
        setSuccess(t.admin.deletedSuccess);
        fetchUsers();
      } else {
        const data = await res.json();
        setError(data.detail || data.error || t.admin.errorDeleting);
      }
    } catch (err) {
      setError(t.admin.connectionError);
    }
  };

  const handleSendRecoveryEmail = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const res = await fetch(`${API_URL}/api/admin/send-recovery`, {
        method: 'POST',
        headers: authHelper.getAuthHeaders(),
        body: JSON.stringify({ email: recoveryEmail })
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(data.message || (language === 'es' ? 'Email de recuperación enviado' : 'Recovery email sent'));
        setShowRecoveryModal(false);
        setRecoveryEmail('');
      } else {
        setError(data.detail || data.error || (language === 'es' ? 'Error al enviar email' : 'Error sending email'));
      }
    } catch (err) {
      setError(t.admin.connectionError);
    }
  };

  const handleToggleAvgCalculator = async (userId, enable) => {
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/features`, {
        method: 'POST',
        headers: authHelper.getAuthHeaders(),
        body: JSON.stringify({ avg_calculator: enable })
      });
      if (res.ok) {
        setSuccess(enable ? 'Calculadora de Refuerzo activada' : 'Calculadora de Refuerzo desactivada');
        fetchUsers();
      } else {
        setError('Error al cambiar la función');
      }
    } catch (e) {
      setError('Error de conexión');
    }
  };

  const handleGrantFreeAccess = async (userId) => {
    if (!window.confirm('¿Otorgar acceso gratis a este usuario?')) return;
    
    setError('');
    setSuccess('');
    
    try {
      const res = await fetch(`${API_URL}/api/admin/subscriptions/${userId}/grant-free`, {
        method: 'POST',
        headers: authHelper.getAuthHeaders()
      });
      
      if (res.ok) {
        setSuccess('Acceso gratis otorgado correctamente');
        fetchUsers();
      } else {
        const data = await res.json();
        setError(data.detail || 'Error al otorgar acceso');
      }
    } catch (err) {
      setError('Error de conexión');
    }
  };

  const handleRevokeFreeAccess = async (userId) => {
    if (!window.confirm('¿Revocar acceso gratis de este usuario?')) return;
    
    setError('');
    setSuccess('');
    
    try {
      const res = await fetch(`${API_URL}/api/admin/subscriptions/${userId}/revoke`, {
        method: 'POST',
        headers: authHelper.getAuthHeaders()
      });
      
      if (res.ok) {
        setSuccess('Acceso revocado correctamente');
        fetchUsers();
      } else {
        const data = await res.json();
        setError(data.detail || 'Error al revocar acceso');
      }
    } catch (err) {
      setError('Error de conexión');
    }
  };


  const downloadAllUsersCSV = () => {
    if (users.length === 0) return;

    const headers = ['ID', language === 'es' ? 'Usuario' : 'Username', 'Email', language === 'es' ? 'Admin' : 'Admin'];
    const rows = users.map(u => [
      u.id || '',
      u.username || '',
      u.email || '',
      u.is_admin ? 'Yes' : 'No'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `users_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const resetForm = () => {
    setUsername('');
    setEmail('');
    setPassword('');
    setShowPassword(false);
    setIsAdmin(false);
  };

  const startEdit = (user) => {
    setEditingUser(user);
    setUsername(user.username);
    setEmail(user.email || '');
    setPassword('');
    setIsAdmin(!!user.is_admin);
    setShowAddModal(true);
  };

  if (loading) {
    return <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="admin-panel">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-xl sm:text-2xl font-black text-blue-900 flex items-center gap-2 uppercase tracking-tight">
          <Users className="text-blue-600" /> {t.admin.title}
        </h2>
        <div className="flex flex-wrap gap-2">
          <button 
            onClick={() => setShowRecoveryModal(true)}
            className="bg-amber-500 hover:bg-amber-600 text-white px-3 sm:px-4 py-2 rounded-lg font-bold flex items-center gap-2 transition-all shadow-lg text-sm"
            data-testid="send-recovery-btn"
          >
            <Mail size={18} /> 
            <span className="hidden sm:inline">{language === 'es' ? 'Enviar Recuperación' : 'Send Recovery'}</span>
          </button>
          <button 
            onClick={downloadAllUsersCSV}
            disabled={users.length === 0}
            className="bg-emerald-500 hover:bg-emerald-600 text-white px-3 sm:px-4 py-2 rounded-lg font-bold flex items-center gap-2 transition-all shadow-lg text-sm disabled:opacity-50"
            data-testid="download-users-btn"
          >
            <Download size={18} />
            <span className="hidden sm:inline">CSV</span>
          </button>
          <button 
            onClick={() => { resetForm(); setEditingUser(null); setShowAddModal(true); }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 rounded-lg font-bold flex items-center gap-2 transition-all shadow-lg text-sm"
            data-testid="add-user-btn"
          >
            <UserPlus size={18} /> {t.admin.addUser}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 sm:p-4 bg-red-50 border border-red-100 text-red-600 rounded-xl flex items-center gap-2 sm:gap-3 text-sm">
          <AlertTriangle size={18} /> {error}
        </div>
      )}

      {success && (
        <div className="p-3 sm:p-4 bg-green-50 border border-green-100 text-green-600 rounded-xl flex items-center gap-2 sm:gap-3 text-sm">
          <CheckCircle2 size={18} /> {success}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          <p className="text-xs font-bold text-gray-500 uppercase">{language === 'es' ? 'Total Usuarios' : 'Total Users'}</p>
          <p className="text-2xl font-black text-blue-600">{users.length}</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          <p className="text-xs font-bold text-gray-500 uppercase">{language === 'es' ? 'Administradores' : 'Admins'}</p>
          <p className="text-2xl font-black text-indigo-600">{users.filter(u => u.is_admin).length}</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          <p className="text-xs font-bold text-gray-500 uppercase">{language === 'es' ? 'Usuarios Std' : 'Std Users'}</p>
          <p className="text-2xl font-black text-emerald-600">{users.filter(u => !u.is_admin).length}</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          <p className="text-xs font-bold text-gray-500 uppercase">{language === 'es' ? 'Con Email' : 'With Email'}</p>
          <p className="text-2xl font-black text-amber-600">{users.filter(u => u.email).length}</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-4 sm:px-6 py-3 sm:py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">ID</th>
                <th className="px-4 sm:px-6 py-3 sm:py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">{t.admin.userEmail}</th>
                <th className="px-4 sm:px-6 py-3 sm:py-4 text-xs font-bold text-gray-500 uppercase tracking-wider hidden sm:table-cell">{t.admin.role}</th>
                <th className="px-4 sm:px-6 py-3 sm:py-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-right">{t.admin.actions}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map(u => (
                <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-500 font-mono">{u.id?.slice(0, 12)}...</td>
                  <td className="px-4 sm:px-6 py-3 sm:py-4">
                    <div className="flex flex-col">
                      <span className="font-bold text-gray-900 text-sm">{u.username}</span>
                      <span className="text-xs text-gray-500">{u.email || t.admin.noEmail}</span>
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-3 sm:py-4 hidden sm:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {u.is_admin ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700">
                          <Shield size={12} /> {t.admin.adminRole}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-gray-100 text-gray-600">
                          <ShieldOff size={12} /> {t.admin.userRole}
                        </span>
                      )}
                      {u.has_free_access && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700">
                          <Gift size={12} /> Gratis
                        </span>
                      )}
                      {u.avg_calculator && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-purple-100 text-purple-700">
                          <CalcFeatureIcon size={12} /> Refuerzo
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-3 sm:py-4 text-right space-x-1 sm:space-x-2">
                    <button
                      onClick={() => handleToggleAvgCalculator(u.id, !u.avg_calculator)}
                      className={`p-1.5 sm:p-2 rounded-lg transition-colors ${u.avg_calculator ? 'text-purple-600 bg-purple-50 hover:bg-purple-100' : 'text-gray-400 hover:bg-gray-100'}`}
                      title={u.avg_calculator ? 'Desactivar Calculadora de Refuerzo' : 'Activar Calculadora de Refuerzo'}
                      data-testid={`toggle-avg-calc-${u.id}`}
                    >
                      <CalcFeatureIcon size={16} />
                    </button>
                    <button 
                      onClick={() => startEdit(u)}
                      className="p-1.5 sm:p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title={t.admin.edit}
                    >
                      <Edit2 size={16} />
                    </button>
                    {!u.is_admin && u.has_free_access && (
                      <button 
                        onClick={() => handleRevokeFreeAccess(u.id)}
                        className="p-1.5 sm:p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                        title="Revocar Acceso Gratis"
                      >
                        <XCircle size={16} />
                      </button>
                    )}
                    {!u.is_admin && !u.has_free_access && (
                      <button 
                        onClick={() => handleGrantFreeAccess(u.id)}
                        className="p-1.5 sm:p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                        title="Otorgar Acceso Gratis"
                      >
                        <Gift size={16} />
                      </button>
                    )}
                    <button 
                      onClick={() => handleDeleteUser(u.id)}
                      className="p-1.5 sm:p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title={t.admin.delete}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-4 sm:p-6 relative">
            <button onClick={() => setShowAddModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
            <h3 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 flex items-center gap-2">
              {editingUser ? <Edit2 className="text-blue-600" /> : <UserPlus className="text-emerald-600" />}
              {editingUser ? t.admin.editUser : t.admin.newUser}
            </h3>
            
            <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser} className="space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-500 uppercase">{t.admin.usernameLabel}</label>
                <input 
                  type="text" 
                  value={username} 
                  onChange={(e) => setUsername(e.target.value)} 
                  className="w-full p-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 text-sm" 
                  required 
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-500 uppercase">{t.admin.emailLabel}</label>
                <input 
                  type="email" 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  className="w-full p-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 text-sm" 
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-500 uppercase">
                  {editingUser ? t.admin.newPasswordLabel : t.admin.passwordLabel}
                </label>
                <div className="relative">
                  <input 
                    type={showPassword ? "text" : "password"} 
                    value={password} 
                    onChange={(e) => setPassword(e.target.value)} 
                    className="w-full p-2 pr-10 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500 text-sm" 
                    required={!editingUser} 
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2 py-2">
                <input 
                  type="checkbox" 
                  id="isAdmin" 
                  checked={isAdmin} 
                  onChange={(e) => setIsAdmin(e.target.checked)} 
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <label htmlFor="isAdmin" className="text-sm font-bold text-gray-700 cursor-pointer">
                  {t.admin.assignAdmin}
                </label>
              </div>
              
              <button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 sm:py-3 rounded-xl font-bold transition-all shadow-lg flex items-center justify-center gap-2 text-sm"
              >
                <Save size={18} /> {editingUser ? t.admin.updateUser : t.admin.createUser}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Send Recovery Email Modal */}
      {showRecoveryModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-4 sm:p-6 relative">
            <button onClick={() => setShowRecoveryModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
            <h3 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 flex items-center gap-2">
              <Mail className="text-amber-600" />
              {language === 'es' ? 'Enviar Email de Recuperación' : 'Send Recovery Email'}
            </h3>
            
            <form onSubmit={handleSendRecoveryEmail} className="space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-500 uppercase">
                  {language === 'es' ? 'Email del Usuario' : "User's Email"}
                </label>
                <input 
                  type="email" 
                  value={recoveryEmail} 
                  onChange={(e) => setRecoveryEmail(e.target.value)} 
                  className="w-full p-2 border rounded-lg outline-none focus:ring-2 focus:ring-amber-500 text-sm" 
                  placeholder="user@example.com"
                  required 
                />
              </div>
              <p className="text-xs text-gray-500">
                {language === 'es' 
                  ? 'Se enviará un enlace de recuperación al email del usuario para que pueda restablecer su contraseña.'
                  : "A recovery link will be sent to the user's email so they can reset their password."}
              </p>
              
              <button 
                type="submit" 
                className="w-full bg-amber-500 hover:bg-amber-600 text-white py-2.5 sm:py-3 rounded-xl font-bold transition-all shadow-lg flex items-center justify-center gap-2 text-sm"
              >
                <Send size={18} /> {language === 'es' ? 'Enviar Email' : 'Send Email'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
